import { useRef, useEffect, useState, memo, useMemo } from "react";
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  useMotionValue,
  useVelocity,
  useAnimationFrame,
  useReducedMotion,
} from "framer-motion";

function wrap(min: number, max: number, v: number) {
  const rangeSize = max - min;
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min;
}

interface ParallaxTextProps {
  children: string;
  baseVelocity?: number;
}

function ParallaxTextComponent({
  children,
  baseVelocity = 100,
}: ParallaxTextProps) {
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    
    // Debounced resize handler
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 150);
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Pause animation when not visible (Intersection Observer)
  useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0, rootMargin: "50px" }
    );
    
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Reduce velocity on mobile for better performance
  const adjustedVelocity = useMemo(() => 
    isMobile ? baseVelocity * 0.4 : baseVelocity,
    [isMobile, baseVelocity]
  );
  
  const baseX = useMotionValue(0);
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 300, // Reduced stiffness for smoother animation
  });
  const velocityFactor = useTransform(
    smoothVelocity, 
    [0, 1000], 
    [0, isMobile ? 1.5 : 3], // Reduced velocity factor
    { clamp: false }
  );

  const x = useTransform(baseX, (v) => `${wrap(-20, -45, v)}%`);

  const directionFactor = useRef<number>(1);

  useAnimationFrame((_t, delta) => {
    // Skip animation if not visible, reduced motion preferred, or tab hidden
    if (!isVisible || prefersReducedMotion || document.hidden) return;
    
    // Cap delta to prevent huge jumps after tab switch
    const cappedDelta = Math.min(delta, 50);
    
    let moveBy = directionFactor.current * adjustedVelocity * (cappedDelta / 1000);

    const velocity = velocityFactor.get();
    if (velocity < 0) {
      directionFactor.current = -1;
    } else if (velocity > 0) {
      directionFactor.current = 1;
    }

    moveBy += directionFactor.current * moveBy * velocity;
    baseX.set(baseX.get() + moveBy);
  });

  // Memoize the content to prevent re-renders
  const textContent = useMemo(() => (
    <>
      <span className="block mr-4 sm:mr-6 md:mr-8 text-white/10">{children}</span>
      <span className="block mr-4 sm:mr-6 md:mr-8 text-white/10">{children}</span>
      <span className="block mr-4 sm:mr-6 md:mr-8 text-white/10">{children}</span>
      <span className="block mr-4 sm:mr-6 md:mr-8 text-white/10">{children}</span>
    </>
  ), [children]);

  return (
    <div 
      ref={containerRef}
      className="overflow-hidden m-0 whitespace-nowrap flex flex-nowrap" 
      aria-hidden="true"
    >
      <motion.div
        style={{ 
          x,
          willChange: isVisible ? "transform" : "auto",
        }}
        className="font-display font-bold uppercase text-4xl sm:text-6xl md:text-8xl lg:text-9xl flex whitespace-nowrap flex-nowrap gpu-accelerated"
      >
        {textContent}
      </motion.div>
    </div>
  );
}

const ParallaxText = memo(ParallaxTextComponent);
export default ParallaxText;